import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, FormControlLabel, Switch, CircularProgress, Box, Typography, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateNews, fetchNewsById } from '../controllers/newsController';

const EditNewsDialog = ({ open, onClose, newsId, onSaved }) => {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [order, setOrder] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !newsId) {
      setTitle('');
      setSummary('');
      setContent('');
      setIsActive(true);
      setOrder(0);
      setError('');
      return;
    }

    let active = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const data = await fetchNewsById(newsId);
        if (!active) return;
        setTitle(data.title || '');
        setSummary(data.summary || '');
        setContent(data.content || '');
        setIsActive(data.isActive ?? true);
        setOrder(typeof data.order === 'number' ? data.order : 0);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Haber alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, newsId]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!newsId) return;
    setError('');
    setSaving(true);
    try {
      await updateNews({ id: newsId, title, summary, content, isActive, order });
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch (err) {
      setError(err?.message || 'Haber güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Haberi Düzenle
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving || loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Başlık
                </Typography>
                <TextField
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder=""
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Özet
                </Typography>
                <TextField
                  fullWidth
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  required
                  placeholder=""
                  multiline
                  minRows={2}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      px: 0,
                      py: 0,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  İçerik
                </Typography>
                <TextField
                  fullWidth
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  placeholder=""
                  multiline
                  minRows={6}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      px: 0,
                      py: 0,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Durum
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={saving}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0E4D94',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0E4D94',
                        },
                      }}
                    />
                  }
                  label={isActive ? 'Aktif' : 'Pasif'}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      ml: 1,
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={onClose}
            disabled={saving || loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || loading || !title || !summary || !content}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditNewsDialog;
